// src/shared/components/ui/Modal.jsx
// Modal reutilizable con overlay, header, body y footer.

import { useEffect, useCallback } from 'react';

/**
 * @param {boolean}          isOpen
 * @param {Function}         onClose
 * @param {string}           [title]
 * @param {React.ReactNode}  children   - Contenido del body
 * @param {React.ReactNode}  [footer]   - Contenido del footer (botones de acción)
 * @param {'sm'|'md'|'lg'|'xl'} [size='md']
 * @param {boolean}          [closeOnOverlay=true]
 * @param {boolean}          [showCloseButton=true]
 */
function Modal({
  isOpen,
  onClose,
  title,
  children,
  footer,
  size = 'md',
  closeOnOverlay = true,
  showCloseButton = true,
}) {
  // Cerrar con Escape
  const handleKeyDown = useCallback(
    (e) => { if (e.key === 'Escape') onClose?.(); },
    [onClose]
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  const maxWidthMap = {
    sm: 'max-w-[420px]',
    md: 'max-w-[560px]',
    lg: 'max-w-[720px]',
    xl: 'max-w-[900px]',
  };
  const maxWidthClass = maxWidthMap[size] || maxWidthMap.md;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? 'modal-title' : undefined}
      className="fixed inset-0 z-[var(--z-modal,200)] flex items-center justify-center p-4 bg-black/50 backdrop-blur-[2px]"
      onClick={closeOnOverlay ? (e) => { if (e.target === e.currentTarget) onClose?.(); } : undefined}
    >
      <div
        className={`flex w-full ${maxWidthClass} max-h-[90vh] flex-col overflow-hidden rounded-[var(--radius-lg)] bg-[var(--color-surface)] shadow-[var(--shadow-lg)] animate-[modal-in_0.2s_ease]`}
      >
        <style>{`
          @keyframes modal-in {
            from { opacity: 0; transform: scale(0.96) translateY(8px); }
            to   { opacity: 1; transform: scale(1)    translateY(0);   }
          }
        `}</style>

        {/* Header */}
        {(title || showCloseButton) && (
          <div className="flex shrink-0 items-center justify-between border-b border-[var(--color-border)] px-6 py-4">
            {title && (
              <h2
                id="modal-title"
                className="m-0 text-lg font-semibold text-[var(--color-text)]"
              >
                {title}
              </h2>
            )}
            {showCloseButton && (
              <button
                onClick={onClose}
                aria-label="Cerrar"
                className="ml-auto flex cursor-pointer items-center rounded-[var(--radius-sm)] border-none bg-transparent p-1 text-[1.2rem] leading-none text-[var(--color-text-muted)] transition-colors hover:text-[var(--color-text)]"
              >
                ✕
              </button>
            )}
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div className="flex shrink-0 justify-end gap-3 border-t border-[var(--color-border)] px-6 py-4">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

export default Modal;
